import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useId, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import { useAuth } from "../context/AuthContext";
import { fetchRemainingMissionsCount } from "./missionsApi";
import {
  buildGuestLoginDestination,
  getRubricForLocation,
  isCentralMissionsPath,
  REMAINING_MISSIONS_LABEL,
  resolveRemainingMissionsDestination,
  sanitizeLocalPath,
} from "./rubricRegistry";
import "./RemainingMissionsButton.css";

const INITIAL_STATE = Object.freeze({
  status: "idle",
  count: null,
});

function stateDescription(state, contract, authenticated, translateMessage, translateLabel) {
  const values = { section: translateLabel(contract.label), count: state.count };
  if (!authenticated) {
    return translateMessage("navigation.missions.guest", `Connectez-vous pour consulter les missions de la rubrique ${contract.label}.`, values);
  }
  if (state.status === "loading") {
    return translateMessage("navigation.missions.loading", `Chargement du nombre de missions restantes pour ${contract.label}.`, values);
  }
  if (state.status === "ready" && state.count === 0) {
    return translateMessage("navigation.missions.empty", `Aucune mission restante dans la rubrique ${contract.label}.`, values);
  }
  if (state.status === "ready") {
    return translateMessage(
      state.count === 1 ? "navigation.missions.remaining_one" : "navigation.missions.remaining_many",
      `${state.count} mission${state.count > 1 ? "s" : ""} restante${state.count > 1 ? "s" : ""} dans la rubrique ${contract.label}.`,
      values
    );
  }
  if (state.status === "unavailable") {
    return translateMessage("navigation.missions.unavailable", `Compteur indisponible pour la rubrique ${contract.label}.`, values);
  }
  if (state.status === "error") {
    return translateMessage("navigation.missions.error", `Erreur lors du chargement du compteur pour la rubrique ${contract.label}.`, values);
  }
  return translateMessage("navigation.missions.view", `Consulter les missions de la rubrique ${contract.label}.`, values);
}

export default function RemainingMissionsButton({ "data-et-copy": __etCopy = "",
  className = "",
  linkClassName = "",
  hideOnMissionsPage = false,
  subtitle = "",
}) {
  useSourceLanguage();
  const location = useLocation();
  const { user } = useAuth();
  const { translateLabel, translateMessage } = useNavigationTranslations();
  const descriptionId = useId();
  const subtitleId = useId();
  const contract = getRubricForLocation(location);
  const [state, setState] = useState(INITIAL_STATE);
  const hidden =
    hideOnMissionsPage && isCentralMissionsPath(location?.pathname || "");
  const authenticated = Boolean(user);
  const missionCountAvailable = contract?.missionCountSource?.available !== false;

  const originPath = useMemo(
    () =>
      sanitizeLocalPath(
        `${location?.pathname || "/"}${location?.search || ""}${
          location?.hash || ""
        }`
      ),
    [location?.hash, location?.pathname, location?.search]
  );

  const missionsDestination = useMemo(
    () =>
      contract
        ? resolveRemainingMissionsDestination(contract, originPath)
        : "/mes-eternua/missions",
    [contract, originPath]
  );
  const destination = authenticated
    ? missionsDestination
    : buildGuestLoginDestination(missionsDestination);

  useEffect(() => {
    if (hidden || !contract || !authenticated) {
      setState(INITIAL_STATE);
      return undefined;
    }
    if (!missionCountAvailable) {
      setState({ status: "unavailable", count: null });
      return undefined;
    }

    const controller = new AbortController();
    setState({ status: "loading", count: null });
    fetchRemainingMissionsCount(contract.rubricId, {
      signal: controller.signal,
    })
      .then((result) => {
        if (!result.available) {
          setState({ status: "unavailable", count: null });
          return;
        }
        setState({ status: "ready", count: result.count });
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setState({ status: "error", count: null });
      });

    return () => controller.abort();
  }, [authenticated, contract, hidden, missionCountAvailable, user?.id, user?.username]);

  if (hidden || !contract || contract.missionButton.visible !== true) return null;

  const description = stateDescription(state, contract, authenticated, translateMessage, translateLabel);
  const badge =
    authenticated && state.status === "ready"
      ? String(state.count)
      : authenticated && state.status === "unavailable"
        ? "—"
        : authenticated && state.status === "error"
          ? "!"
          : null;
  const visibleSubtitle = authenticated && state.status === "ready"
    ? state.count === 0
      ? translateLabel("Tout est à jour")
      : translateMessage(
        state.count === 1 ? "navigation.missions.active_one" : "navigation.missions.active_many",
        `${state.count} mission${state.count > 1 ? "s" : ""} active${state.count > 1 ? "s" : ""}`,
        { count: state.count }
      )
    : !authenticated
      ? translateLabel("Connexion requise")
      : translateLabel(subtitle);
  const describedBy = [visibleSubtitle ? subtitleId : null, descriptionId]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`remaining-missions remaining-missions--${state.status}${
        className ? ` ${className}` : ""
      }`}
      data-rubric={contract.rubricId}
      data-state={authenticated ? state.status : "guest"}
      aria-busy={authenticated && state.status === "loading" ? "true" : "false"}
    >
      <Link
        className={`remaining-missions__link${
          linkClassName ? ` ${linkClassName}` : ""
        }`}
        to={destination}
        aria-label={translateLabel(REMAINING_MISSIONS_LABEL)}
        aria-describedby={describedBy}
      >
        <span className="remaining-missions__spark" aria-hidden="true">✦</span>
        <span className="remaining-missions__copy">
          <span className="remaining-missions__label">{translateLabel(REMAINING_MISSIONS_LABEL)}</span>
          {visibleSubtitle ? (
            <small className="remaining-missions__subtitle" id={subtitleId}>
              {visibleSubtitle}
            </small>
          ) : null}
        </span>
        {authenticated && state.status === "loading" ? (
          <span className="remaining-missions__spinner" aria-hidden="true" />
        ) : null}
        {badge !== null ? (
          <span className="remaining-missions__count" aria-hidden="true">{badge}</span>
        ) : null}
        <span className="remaining-missions__arrow" aria-hidden="true">→</span>
      </Link>
      <span
        className="remaining-missions__status"
        id={descriptionId}
        role={state.status === "error" ? "alert" : "status"}
        aria-live="polite"
      >
        {description}
      </span>
    </div>
  );
}
